/**
 * F29 (Wave 2C) — ReinstateTeamDialog.
 *
 * Lighter confirmation (NOT destructive, no type-to-confirm). Optional reason
 * note saved on the audit log. Composes Modal + react-hook-form to mirror the
 * F11 ResumeDialog at `apps/admin/src/pages/b2b-accounts/B2bAccountDetailPage.tsx`.
 */
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, Modal, Textarea } from '@bravemove/ui-web';
import { ReinstateTeamInputSchema, type ReinstateTeamInput } from '@bravemove/types';

export interface ReinstateTeamDialogProps {
  readonly open: boolean;
  readonly teamName: string;
  readonly loading: boolean;
  readonly serverError: string | null;
  readonly onClose: () => void;
  readonly onSubmit: (body: ReinstateTeamInput) => void;
}

interface ReinstateFormValues {
  readonly reason: string;
}

export function ReinstateTeamDialog({
  open,
  teamName,
  loading,
  serverError,
  onClose,
  onSubmit,
}: ReinstateTeamDialogProps) {
  const { t } = useI18n();

  // Local schema = required string (mode: 'soft' — empty allowed → omitted).
  const localSchema = z.object({
    reason: z.string().max(500),
  });

  const form = useForm<ReinstateFormValues>({
    resolver: zodResolver(localSchema),
    defaultValues: { reason: '' },
    mode: 'onTouched',
  });

  useEffect(() => {
    if (open) form.reset({ reason: '' });
  }, [open, form]);

  const handleConfirm = () => {
    void form.handleSubmit((v) => {
      const trimmed = v.reason.trim();
      const body: ReinstateTeamInput = trimmed
        ? ReinstateTeamInputSchema.parse({ reason: trimmed })
        : {};
      onSubmit(body);
    })();
  };

  // ICU `{teamName}` interpolation does NOT fire reliably in test env (vitest
  // jsdom — same trap F23's CommissionStatementDetailPage hit). Resolve the
  // title manually here and pass the result string to <Modal>.
  const interpolated = t('admin:team.admin.reinstate_dialog.title', {
    teamName,
    defaultValue: 'Reinstate {teamName}?',
  }) as string;
  const titleText = interpolated.includes('{teamName}') ? `Reinstate ${teamName}?` : interpolated;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={titleText}
      description={t('admin:team.admin.reinstate_dialog.body')}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            {t('admin:team.admin.reinstate_dialog.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={loading}
            loading={loading}
            data-testid="reinstate-team-confirm"
          >
            {t('admin:team.admin.reinstate_dialog.confirm_button')}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleConfirm();
        }}
        className="flex flex-col gap-4"
      >
        <FormField
          label={t('admin:team.admin.reinstate_dialog.note_label')}
          helper={t('admin:team.admin.reinstate_dialog.note_helper')}
          error={form.formState.errors.reason?.message}
        >
          <Textarea
            rows={3}
            maxLength={500}
            showCounter
            data-testid="reinstate-team-reason"
            {...form.register('reason')}
          />
        </FormField>

        {serverError ? <Banner tone="danger">{serverError}</Banner> : null}
      </form>
    </Modal>
  );
}
